import { useEffect, useRef } from "react";
import { ShipmentType } from "entities/shared/shipmentCard.types";
import { ShipmentModel } from "features/shared/shipmentCard.types";
import { GetAirCardsFunc } from "entities/AirSavedCard";
import { AirSearchRequestFunc } from "entities/AirSearchCard";
import { ParcelSearchRequestFunc } from "entities/ParcelSearchCard";
import { RailSearchRequestFunc } from "entities/RailSearchCard";
import { RoadSearchRequestFunc } from "entities/RoadSearchCard";
import { searchFilterStore } from "entities/SearchFilter";
import { GetSeaCardsFunc } from "entities/SeaSavedCard";
import { SeaSearchRequestFunc } from "entities/SeaSearchCard";
import { useAppSelector } from "shared/lib/redux";
import { useAirUrlQueryBasedInitialSearch } from "./AirQueryParams/useAirUrlQueryHandlerInitialSearch";
import { getInitialSearchQPM } from "./ParamsManager";
import { useParcelUrlQueryBasedInitialSearch } from "./ParcelQueryParams/useParcelUrlQueryHandlerInitialSearch";
import { useRailUrlQueryBasedInitialSearch } from "./RailQueryParams/useRailUrlQueryHandlerInitialSearch";
import { useRoadUrlQueryBasedInitialSearch } from "./RoadQueryParams/useRoadUrlQueryHandlerInitialSearch";
import { useSeaUrlQueryBasedInitialSearch } from "./SeaQueryParams/useSeaUrlQueryHandlerInitialSearch";

import { getAirReferenceFromUrl, getSeaReferenceFromUrl } from "./urlQueryParam.utils";

type UseInitialSearchProps = {
  seaSearchController: {
    searchRequest: SeaSearchRequestFunc;
    getCards: GetSeaCardsFunc;
    isSearchLoading: boolean;
  };
  airSearchController: {
    searchRequest: AirSearchRequestFunc;
    getCards: GetAirCardsFunc;
    isSearchLoading: boolean;
  };
  railSearchController: {
    searchRequest: RailSearchRequestFunc;
    // getCards: GetRailCardsFunc;
    isSearchLoading: boolean;
  };
  roadSearchController: {
    searchRequest: RoadSearchRequestFunc;
    // getCards: GetRoadCardsFunc;
    isSearchLoading: boolean;
  };
  parcelSearchController: {
    searchRequest: ParcelSearchRequestFunc;
    // getCards: GetParcelCardsFunc;
    isSearchLoading: boolean;
  };
  openedCardAction: (card: ShipmentModel | null) => { type: string; payload: ShipmentModel | null };
  isSearchTypeParamInitialized: boolean;
  isAuthResolved: boolean;
};

export const useInitialSearch = ({
  seaSearchController,
  airSearchController,
  railSearchController,
  roadSearchController,
  parcelSearchController,
  openedCardAction,
  isSearchTypeParamInitialized,
  isAuthResolved,
}: UseInitialSearchProps) => {
  const shipment = useAppSelector(searchFilterStore.selectors.shipment);
  const { seaInitialSearch } = useSeaUrlQueryBasedInitialSearch({
    searchController: {
      searchRequest: seaSearchController.searchRequest,
      getCards: seaSearchController.getCards,
      isSearchLoading: seaSearchController.isSearchLoading,
    },
    openedCardAction: openedCardAction,
  });

  const { airInitialSearch } = useAirUrlQueryBasedInitialSearch({
    searchController: {
      searchRequest: airSearchController.searchRequest,
      getCards: airSearchController.getCards,
      isSearchLoading: airSearchController.isSearchLoading,
    },
    openedCardAction: openedCardAction,
  });

  const { railInitialSearch } = useRailUrlQueryBasedInitialSearch({
    searchController: {
      searchRequest: railSearchController.searchRequest,
      isSearchLoading: railSearchController.isSearchLoading,
    },
    openedCardAction: openedCardAction,
  });

  const { roadInitialSearch } = useRoadUrlQueryBasedInitialSearch({
    searchController: {
      searchRequest: roadSearchController.searchRequest,
      isSearchLoading: roadSearchController.isSearchLoading,
    },
    openedCardAction: openedCardAction,
  });

  const { parcelInitialSearch } = useParcelUrlQueryBasedInitialSearch({
    searchController: {
      searchRequest: parcelSearchController.searchRequest,
      isSearchLoading: parcelSearchController.isSearchLoading,
    },
    openedCardAction: openedCardAction,
  });

  const initialSearchTriggeredRef = useRef(false);

  useEffect(() => {
    if (!isSearchTypeParamInitialized || !isAuthResolved) return;

    const isAnySearchLoading =
      seaSearchController.isSearchLoading ||
      airSearchController.isSearchLoading ||
      railSearchController.isSearchLoading ||
      roadSearchController.isSearchLoading ||
      parcelSearchController.isSearchLoading;

    if (isAnySearchLoading || initialSearchTriggeredRef.current) return;

    const qpm = getInitialSearchQPM();
    switch (shipment) {
      case ShipmentType.AIR:
        const airReferenceQueryValue = getAirReferenceFromUrl(qpm);
        if (!airReferenceQueryValue) return;
        initialSearchTriggeredRef.current = true;
        airInitialSearch({ requestedFromUrl: true });
        break;
      case ShipmentType.SEA:
        const seaReferenceQueryValue = getSeaReferenceFromUrl(qpm);
        if (!seaReferenceQueryValue) return;
        initialSearchTriggeredRef.current = true;
        seaInitialSearch({ requestedFromUrl: true });
        break;
      case ShipmentType.RAIL:
        const railReferenceQueryValue = qpm.get("number");
        if (!railReferenceQueryValue) return;
        initialSearchTriggeredRef.current = true;
        railInitialSearch({ requestedFromUrl: true });
        break;
      case ShipmentType.ROAD:
        const roadReferenceQueryValue = qpm.get("number");
        if (!roadReferenceQueryValue) return;
        initialSearchTriggeredRef.current = true;
        roadInitialSearch({ requestedFromUrl: true });
        break;
      case ShipmentType.PARCEL:
        const parcelReferenceQueryValue = qpm.get("number");
        if (!parcelReferenceQueryValue) return;
        initialSearchTriggeredRef.current = true;
        parcelInitialSearch({ requestedFromUrl: true });
        break;
      default:
        break;
    }
  }, [
    isSearchTypeParamInitialized,
    isAuthResolved,
    shipment,
    seaSearchController.isSearchLoading,
    airSearchController.isSearchLoading,
    railSearchController.isSearchLoading,
    roadSearchController.isSearchLoading,
    parcelSearchController.isSearchLoading,
  ]);
};
